import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PASSWORD_MIN_LENGTH, type SessionResponse } from '@bingo/shared';
import { AuthCard } from '../../components/AuthCard.js';
import { ApiError, api } from '../../lib/api.js';
import { adoptSession } from '../../lib/session.js';

/**
 * /redefinir/:token — senha nova por link de uso único.
 *
 * Mesmo mecanismo do convite (ver `modules/people/invites.ts`), com validade
 * mais curta: 2 horas. A redefinição é pedida quando a pessoa está ali,
 * esperando — um link de dois dias para trocar senha seria superfície aberta
 * sem necessidade.
 */

interface ResetPublic {
  email: string;
  name: string | null;
  organizationName: string;
  expiresAt: string;
}

export function Redefinir() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [reset, setReset] = useState<ResetPublic | null | 'invalid'>(null);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;

    void api<ResetPublic>(`/api/resets/${token}`, { anonymous: true })
      .then(setReset)
      .catch(() => setReset('invalid'));
  }, [token]);

  if (reset === null) {
    return (
      <AuthCard title="Redefinir acesso">
        <p className="hint-text">Carregando…</p>
      </AuthCard>
    );
  }

  if (reset === 'invalid') {
    return (
      <AuthCard
        title="Link indisponível"
        subtitle="Este link não vale mais — pode ter vencido ou já ter sido usado."
      >
        <p className="hint-text">Peça um link novo a quem administra a organização.</p>
      </AuthCard>
    );
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;

    setBusy(true);
    setError(null);

    try {
      const session = await api<SessionResponse>(`/api/resets/${token}/accept`, {
        method: 'POST',
        anonymous: true,
        body: { password },
      });

      adoptSession(session);
      navigate('/inicio', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível redefinir a senha.');
      setBusy(false);
    }
  };

  return (
    <AuthCard
      title="Criar senha nova"
      subtitle={`Conta ${reset.email}, em ${reset.organizationName}.`}
    >
      <form className="auth-form" onSubmit={(event) => void submit(event)} noValidate>
        <label className="field">
          <span>Senha nova</span>
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="new-password"
            minLength={PASSWORD_MIN_LENGTH}
            required
            autoFocus
          />
        </label>
        <p className="hint-text">
          Pelo menos {PASSWORD_MIN_LENGTH} caracteres. Os outros aparelhos onde esta conta estava
          aberta serão desconectados.
        </p>

        {error ? (
          <p className="error" role="alert">
            {error}
          </p>
        ) : null}

        <button type="submit" className="btn primary" disabled={busy}>
          {busy ? 'Salvando…' : 'Salvar e entrar'}
        </button>
      </form>
    </AuthCard>
  );
}
